import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { type BaseChainflipAsset } from '@chainflip/utils/chainflip';
import type BigNumber from 'bignumber.js';
import classNames from 'classnames';
import { MacScrollbar } from 'mac-scrollbar';
import LayersIcon from '@/shared/assets/svg/layers-icon-with-shadow.svg';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import { TableV2 } from '@/shared/components/molecules/TableV2';
import {
  type OpenOrderItem,
  RpcLimitOrderRow,
  RpcRangeOrderRow,
} from '@/shared/components/orders/RpcOrdersTable';
import { TABLE_POLL_INTERVAL } from '@/shared/constants';
import { getCachePoolsQuery } from '@/shared/graphql/cache/pools';
import { type PoolOrderFragment } from '@/shared/graphql/generated/lp/graphql';
import { useChainflipAssetPrices, useTableSort } from '@/shared/hooks';
import useFrontendPagination from '@/shared/hooks/useFrontendPagination';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useTracking from '@/shared/hooks/useTracking';
import { addValueToRpcOrders, chainflipAssetMap, COMPARATORS, type RpcOrder } from '@/shared/utils';
import { CloseLimitOrderButton } from './CloseLimitOrderButton';
import { CloseRangeOrderButton } from './CloseRangeOrderButton';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../../types/track';

const SORT_FUNCTIONS: ((order: OpenOrderItem) => string | BigNumber | number)[] = [
  () => 0,
  () => 0,
  (order) => order.valueUsd,
  () => 0,
  () => 0,
  () => 0,
  (order) => order.createdAt ?? '',
  () => 0,
];

export const OpenOrdersTableMobile = ({
  rpcOrders,
  processorOrders,
}: {
  rpcOrders: RpcOrder[];
  processorOrders: PoolOrderFragment[] | undefined;
}) => {
  const { data: poolData } = useGqlQuery(getCachePoolsQuery, {
    context: { clientName: 'statechainCache' },
    refetchInterval: TABLE_POLL_INTERVAL,
  });
  const { prices: assetPrices } = useChainflipAssetPrices();

  const orders = addValueToRpcOrders({
    rpcOrders,
    processorOrders,
    poolData: poolData?.pools?.nodes,
    assetPrices,
  });

  const { sort, setCol } = useTableSort({ initialSort: { col: 6, direction: 'DESC' } });

  const sortedOrders = useMemo(() => {
    const { col, direction } = sort;
    return orders.toSorted((a, b) =>
      COMPARATORS[direction](SORT_FUNCTIONS[col](a), SORT_FUNCTIONS[col](b)),
    );
  }, [sort, orders]);

  const { data: paginatedData, paginationControls } = useFrontendPagination(sortedOrders, {
    initialLimit: 10,
    paginationLimits: [10, 20, 30],
  });

  return (
    <TableV2
      columns={[]}
      sorting={{ setCol, sort }}
      tbodyClassName="[&>tr]:border-cf-gray-3-5 [&>tr:not(:last-child)]:border-b"
      emptyRowsContent={
        <div className="m-10 flex flex-col items-center gap-y-2 py-4">
          <LayersIcon />
          <span className="flex items-center gap-x-1.5 text-14 text-cf-light-2">
            No open positions here yet.
          </span>
        </div>
      }
      rows={paginatedData.edges.map(({ node: order }) =>
        order.type === 'range' ? (
          <RpcRangeOrderRow isMobileView order={order} baseAsset={order.asset} />
        ) : (
          <RpcLimitOrderRow isMobileView order={order} baseAsset={order.asset} />
        ),
      )}
      paginationControls={paginationControls}
    />
  );
};

export const OpenOrdersTable = ({
  rpcOrders,
  processorOrders,
  onRowClick,
  asset,
  className,
  containerClassName,
}: {
  rpcOrders: RpcOrder[];
  processorOrders: PoolOrderFragment[] | undefined;
  onRowClick?: (asset: BaseChainflipAsset) => void;
  asset?: BaseChainflipAsset;
  className?: string;
  containerClassName?: string;
}) => {
  const router = useRouter();
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: polkadotAccount } = usePolkadot();

  const { data: poolData } = useGqlQuery(getCachePoolsQuery, {
    context: { clientName: 'statechainCache' },
    refetchInterval: TABLE_POLL_INTERVAL,
  });
  const { prices: assetPrices } = useChainflipAssetPrices();

  const orders = addValueToRpcOrders({
    rpcOrders,
    processorOrders,
    poolData: poolData?.pools?.nodes,
    assetPrices,
  });

  const { sort, setCol } = useTableSort({ initialSort: { col: 6, direction: 'DESC' } });

  const sortedOrders = useMemo(() => {
    const { col, direction } = sort;
    return orders.toSorted((a, b) =>
      COMPARATORS[direction](SORT_FUNCTIONS[col](a), SORT_FUNCTIONS[col](b)),
    );
  }, [sort, orders]);

  return (
    <MacScrollbar
      skin="dark"
      className={classNames(
        'h-full max-h-[405px] overflow-y-scroll rounded-b-md border-b border-b-cf-gray-3-5',
        containerClassName,
      )}
    >
      <TableV2
        columns={[
          { name: 'Type', sortable: false },
          { name: 'Assets / Amount', sortable: false },
          { name: 'Value', sortable: true },
          { name: 'Price / Range', sortable: false },
          { name: 'Earned fees', sortable: false },
          { name: 'Status', sortable: false },
          { name: 'Created', sortable: true },
          { name: '', sortable: false },
        ]}
        sorting={{
          setCol,
          sort,
        }}
        emptyRowsContent={
          <div className="m-10 flex flex-col items-center gap-y-2 py-4">
            <LayersIcon />
            <span className="flex items-center gap-x-1.5 text-14 text-cf-light-2">
              No open {asset && <TokenWithChainByAsset asset={asset} />} positions here yet.
            </span>
            {router.asPath.startsWith('/portfolio') && (
              <ReadonlyButton
                type="secondary-standard"
                onClick={() => {
                  track(LiquidityProvisionEvents.AddPosition, {
                    props: {
                      path: window.location.pathname,
                      scAccount: polkadotAccount?.idSs58 ?? '-',
                      position: 'dashboard_positions',
                    },
                  });
                  router.push(`/market/${chainflipAssetMap.Btc.chainflipId}`);
                }}
              >
                Create a Position
              </ReadonlyButton>
            )}
          </div>
        }
        rows={sortedOrders.map((order) =>
          order.type === 'range' ? (
            <RpcRangeOrderRow
              key={order.id}
              baseAsset={order.asset}
              order={order}
              additionalCells={
                <td>
                  <div className="flex justify-end">
                    <CloseRangeOrderButton order={order} baseAsset={order.asset} />
                  </div>
                </td>
              }
              onClick={onRowClick}
            />
          ) : (
            <RpcLimitOrderRow
              key={order.id}
              baseAsset={order.asset}
              order={order}
              additionalCells={
                <td>
                  <div className="flex justify-end">
                    <CloseLimitOrderButton order={order} baseAsset={order.asset} />
                  </div>
                </td>
              }
              onClick={onRowClick}
            />
          ),
        )}
        className={className}
      />
    </MacScrollbar>
  );
};
